import { useState } from 'react'
import { ReactiveCallGuidance } from './ReactiveCallGuidance'
import { createConversation, reduceConversation, type ConversationAction, type ConversationState, type Plan } from './lib/live-conversation/reducer'

const PRACTICE_PRICES: Record<Plan, number> = { watch: 79, protect: 149, shield: 299 }

function newPractice(): ConversationState {
  return {
    ...createConversation({ contactId: 'synthetic-roleplay-owner', sessionId: `roleplay-${crypto.randomUUID()}` },undefined,9),
    rep: 'Practice rep',
    researchObservation: 'In this synthetic scenario, the business has a public contact form.',
  }
}

/** In-memory practice only. No canonical CRM hook, storage, or provider is used. */
export function RoleplayTraining() {
  const [state, setState] = useState<ConversationState>(newPractice)
  const [generation, setGeneration] = useState(0)
  const [simulation, setSimulation] = useState<'schedule' | 'checkout' | null>(null)
  const [notice, setNotice] = useState('')

  const act = (action: ConversationAction) => setState(current => reduceConversation(current, action))
  const reset = () => {
    setState(newPractice())
    setGeneration(value => value + 1)
    setSimulation(null)
    setNotice('Practice reset to the opener. Real CRM work was not changed.')
  }
  const simulateSchedule = () => {
    const startsAt=state.v9?.facts.callbackAt,timeZone=state.v9?.facts.callbackZone
    if(!startsAt||!timeZone){setNotice('Record the synthetic requested callback time and zone before simulating a save.');return}
    act({ type: 'schedule-confirmed', eventId: crypto.randomUUID(), startsAt, timeZone })
    setSimulation(null)
    setNotice('Simulated appointment saved in practice memory only. No CRM appointment exists.')
  }

  return <div className="page roleplay-training-page" data-practice-context="synthetic">
    <div className="page-heading"><div><h1>Roleplay Training</h1><p>Practice the current guided call with a teammate playing a synthetic business owner.</p></div><button type="button" onClick={reset}>Reset practice</button></div>
    <section className="panel" aria-label="Practice boundaries">
      <strong>SYNTHETIC PRACTICE — no real contact</strong>
      <p>All answers, notes, outcomes and receipts stay in memory here. Training cannot dial, send messages, claim tasks, save customer notes, create appointments or process payments.</p>
      <p>All prices shown here are practice examples, not a customer quote. Use supported scope and evidence in the conversation.</p>
      <small>Reset, refresh or leaving this page clears practice. Your real work is unaffected.</small>
    </section>
    {notice && <p role="status">{notice}</p>}
    {state.callOutcome && <p role="status">Practice outcome: {state.callOutcome}. This does not disposition a CRM call.</p>}
    {simulation === 'schedule' && <section className="panel" aria-label="Scheduling simulation">
      <h2>Scheduling simulation</h2>
      <p>In a real call, use the existing CRM scheduling controls. Nothing is booked until those controls save successfully.</p>
      <p>Practice continuation: {state.schedulingRequest?.resumeNodeId ?? state.currentMainNodeId}.</p>
      <button type="button" disabled={!state.schedulingRequest || Boolean(state.callOutcome)} onClick={simulateSchedule}>Simulate scheduling success</button>
      <button type="button" onClick={() => { setSimulation(null); setNotice('Scheduling was not saved. Practice continuation remains available; no appointment exists.') }}>Simulate scheduling not saved</button>
    </section>}
    {simulation === 'checkout' && <section className="panel" aria-label="Payment simulation">
      <h2>Close / Payment simulation</h2>
      <p>No checkout link was opened and no payment was processed. Continue through the guide and choose the synthetic owner’s response. A payment report is still not verified payment.</p>
      <button type="button" onClick={() => setSimulation(null)}>Close simulation</button>
    </section>}
    <section className="panel script-workbench reactive-call-center" aria-label="Practice guided conversation">
      <ReactiveCallGuidance key={generation} state={state} disabled={false} status="success" error=""
        onAction={act} onRetry={() => {}}
        onPlan={(plan, lower) => act({ type: 'configure', values: { recommendedPlan: plan, price: PRACTICE_PRICES[plan], ...(lower ? { lowerPlan: plan } : {}) } })}
        onCheckout={() => { setSimulation('checkout'); setNotice('Practice Close / Payment opened. No live billing action occurred.') }}
        onSchedule={() => { setSimulation('schedule'); setNotice('Practice scheduling opened. No appointment exists yet.') }}
      />
    </section>
  </div>
}
